import { useState } from "react";
import { IClosure } from "@/api/businessLinesApi";
import { format, differenceInCalendarDays, parseISO } from "date-fns";

interface DefaultClosuresProps {
  closures: IClosure[];
  setClosures: React.Dispatch<React.SetStateAction<IClosure[]>>;
  defaultClosures: IClosure[];
  businessLineId: string | null;
  isReadOnly?: boolean;
}

export default function DefaultClosures({
  closures,
  setClosures,
  defaultClosures,
  businessLineId,
  isReadOnly = false,
}: DefaultClosuresProps) {
  // Custom closure form state
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customTitle, setCustomTitle] = useState("");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const handleClosureToggle = (closure: IClosure) => {
    const closureIdStr = String(closure.id);
    const exists = closures.some((c) => c.id === closureIdStr);
    if (exists) {
      setClosures((prev) => prev.filter((c) => c.id !== closureIdStr));
    } else {
      setClosures((prev) => [
        ...prev,
        {
          id: closureIdStr,
          title: closure.title,
          start: closure.start,
          end: closure.end,
          is_default: closure.is_default,
          business_line_id: closure.is_default ? null : "",
        },
      ]);
    }
  };

  const handleAddCustomClosure = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customTitle || !customStart || !customEnd) return;
    setClosures((prev) => [
      ...prev,
      {
        id: null,
        title: customTitle,
        start: customStart,
        end: customEnd,
        is_default: false,
        business_line_id: businessLineId,
      },
    ]);
    setShowCustomForm(false);
    setCustomTitle("");
    setCustomStart("");
    setCustomEnd("");
  };

  return (
    <div className="flex flex-col gap-4 w-full justify-between">
      <div className="flex flex-col justify-start items-start gap-3">
        {/* Show default closures */}
        {defaultClosures.map((closure) => {
          const startDate = parseISO(closure.start);
          const endDate = parseISO(closure.end);
          const days = differenceInCalendarDays(endDate, startDate);
          return (
            <div key={closure.id} className="flex items-center gap-56">
              <div className="size-6 flex justify-center items-center overflow-hidden">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={closures.some((c) => c.id === String(closure.id))}
                    onChange={() => handleClosureToggle(closure)}
                    aria-label={`Toggle ${closure.title} closure`}
                    disabled={isReadOnly}
                    className={`size-5 p-[3.23px] rounded-xs shadow-[inset_0.8086643218994141px_0.8086643218994141px_1.4151625633239746px_0px_rgba(0,0,0,0.00)] outline outline-[0.61px] flex justify-center items-center overflow-hidden appearance-none ${
                      closures.some((c) => c.id === String(closure.id))
                        ? "outline-[#73DCB0]"
                        : "bg-white outline-neutral-200"
                    }`}
                    style={
                      closures.some((c) => c.id === String(closure.id))
                        ? { backgroundColor: "#73DCB0" }
                        : {}
                    }
                  />
                  {closures.some((c) => c.id === String(closure.id)) && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <svg
                        className="w-3 h-3 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
              <div className="w-20 text-zinc-700 text-sm font-normal leading-tight capitalize">
                {closure.title}
              </div>
              <div className="w-40 text-right text-gray-500 text-sm font-normal leading-tight">
                {format(startDate, "d MMM yyyy")}
                {days > 1 && (
                  <>
                    {" - "}
                    {format(endDate, "d MMM yyyy")} ({days + 1} days)
                  </>
                )}
              </div>
            </div>
          );
        })}
        {/* Show custom closures */}
        {closures
          .filter((closure) => !closure.is_default)
          .map((closure) => {
            const startDate = parseISO(closure.start);
            const endDate = parseISO(closure.end);
            const days = differenceInCalendarDays(endDate, startDate);
            return (
              <div key={closure.id} className="flex items-center gap-56">
                <div className="size-6 flex justify-center items-center overflow-hidden">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={true}
                      onChange={() => {
                        setClosures((prev) =>
                          prev.filter((c) => c.id !== closure.id)
                        );
                      }}
                      aria-label={`Remove ${closure.title} closure`}
                      disabled={isReadOnly}
                      className="size-5 p-[3.23px] rounded-xs shadow-[inset_0.8086643218994141px_0.8086643218994141px_1.4151625633239746px_0px_rgba(0,0,0,0.00)] outline outline-[0.61px] outline-[#73DCB0] flex justify-center items-center overflow-hidden appearance-none"
                      style={{ backgroundColor: "#73DCB0" }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <svg
                        className="w-3 h-3 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="w-20 text-zinc-700 text-sm font-normal leading-tight capitalize">
                  {closure.title}
                </div>
                <div className="w-40 text-right text-gray-500 text-sm font-normal leading-tight">
                  {format(startDate, "d MMM yyyy")}
                  {days > 1 && (
                    <>
                      {" - "}
                      {format(endDate, "d MMM yyyy")} ({days + 1} days)
                    </>
                  )}
                </div>
              </div>
            );
          })}
      </div>
      {/* Add custom closure button and form */}
      {!isReadOnly && !showCustomForm ? (
        <button
          className="mt-2 px-4 py-2 bg-gray-100 rounded hover:bg-gray-200 text-sm"
          onClick={() => setShowCustomForm(true)}
        >
          + Add a custom closure day
        </button>
      ) : !isReadOnly && showCustomForm ? (
        <form
          className="flex flex-col gap-2 mt-2"
          onSubmit={handleAddCustomClosure}
        >
          <input
            type="text"
            placeholder="Event name"
            value={customTitle}
            onChange={(e) => setCustomTitle(e.target.value)}
            className="border rounded px-2 py-1"
            required
            aria-label="Custom closure event name"
            disabled={isReadOnly}
          />
          <div className="flex gap-2">
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="border rounded px-2 py-1"
              required
              aria-label="Custom closure start date"
              disabled={isReadOnly}
            />
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="border rounded px-2 py-1"
              required
              aria-label="Custom closure end date"
              disabled={isReadOnly}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              className="px-4 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
            >
              Add
            </button>
            <button
              type="button"
              className="px-4 py-1 bg-gray-200 rounded hover:bg-gray-300 text-sm"
              onClick={() => setShowCustomForm(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
